import { Link } from 'react-router-dom'
import { ArrowRight, FileCheck2, Lock, Webhook, Zap } from 'lucide-react'
import { ROUTES } from '@/lib/constants'

export function LandingPage() {
  return (
    <div className="min-h-screen bg-bg-base text-fg-primary">
      <header className="border-b border-border">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-md bg-brand-500 font-mono text-sm font-bold text-white">
              Tx
            </div>
            <span className="text-base font-semibold">Taxor Partners</span>
          </div>
          <div className="flex items-center gap-3">
            <Link to={ROUTES.LOGIN} className="text-sm font-medium text-fg-secondary hover:text-fg-primary">
              Iniciar sesión
            </Link>
            <Link
              to={ROUTES.SIGNUP}
              className="inline-flex items-center gap-1.5 rounded-md bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700"
            >
              Crear cuenta
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-4xl px-6 py-20">
        <div className="text-center">
          <p className="font-mono text-xs uppercase tracking-wider text-brand-600">
            ▸ API DIAN para ERPs
          </p>
          <h1 className="mt-3 text-4xl font-bold tracking-tight md:text-5xl">
            Facturación electrónica DIAN
            <br />
            como API, sin construirla.
          </h1>
          <p className="mx-auto mt-5 max-w-2xl text-base text-fg-secondary">
            Conecta tu ERP a Taxor en menos de 1 día. Nosotros firmamos, transmitimos y
            entregamos. Tú emites como siempre. Sin certificado propio, sin SOAP DIAN,
            sin XAdES.
          </p>
          <div className="mt-8 flex justify-center gap-3">
            <Link
              to={ROUTES.SIGNUP}
              className="inline-flex items-center gap-1.5 rounded-md bg-brand-600 px-6 py-3 text-base font-medium text-white hover:bg-brand-700"
            >
              Empezar gratis
              <ArrowRight className="h-4 w-4" />
            </Link>
            <a
              href="/openapi.yaml"
              target="_blank"
              rel="noopener"
              className="inline-flex items-center gap-1.5 rounded-md border border-border px-6 py-3 text-base font-medium text-fg-secondary hover:bg-bg-elevated"
            >
              Ver OpenAPI
            </a>
          </div>
        </div>

        <div className="mt-16 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          <Feature icon={Zap} title="Setup en 1 día">
            Self-onboarding, API keys al instante, sandbox DIAN-hab antes de prod.
          </Feature>
          <Feature icon={Lock} title="Tu cert, no el tuyo">
            Modelo mandato DIAN: nosotros firmamos con cert ECD propio. Cero
            trámite ECD para ti.
          </Feature>
          <Feature icon={FileCheck2} title="Audit court-ready">
            Hash chain inmutable de aceptaciones legales. Habeas Data Ley 1581.
            Retención 5 años DIAN.
          </Feature>
          <Feature icon={Webhook} title="Webhooks robustos">
            HMAC signature + replay protection + dead-letter queue + replay
            manual desde dashboard.
          </Feature>
        </div>

        <div className="mt-20 rounded-xl border border-border bg-bg-surface p-6">
          <h2 className="text-lg font-semibold">Honestidad: dónde estamos hoy</h2>
          <p className="mt-2 text-sm text-fg-secondary">
            Taxor es <strong>PYME-grade</strong> hoy. Excelente para empresas COL de 5-200
            empleados. Roadmap a SOC 2 / ISO 27001 / multi-region HA disponible
            público en{' '}
            <a
              href="/sla.json"
              target="_blank"
              rel="noopener"
              className="text-brand-600 underline"
            >
              /sla.json
            </a>{' '}
            y{' '}
            <a
              href="/changelog"
              target="_blank"
              rel="noopener"
              className="text-brand-600 underline"
            >
              /changelog
            </a>
            . Si necesitas enterprise hoy, hablemos antes de firmar.
          </p>
        </div>
      </main>

      <footer className="mt-20 border-t border-border py-6 text-center text-xs text-fg-muted">
        © 2026 Sociedad Colombiana de Consultoría S.A.S. — Proveedor Tecnológico autorizado DIAN
      </footer>
    </div>
  )
}

function Feature({
  icon: Icon,
  title,
  children,
}: {
  icon: React.ComponentType<{ className?: string }>
  title: string
  children: React.ReactNode
}) {
  return (
    <div className="rounded-lg border border-border bg-bg-surface p-5">
      <Icon className="h-6 w-6 text-brand-600" />
      <h3 className="mt-3 text-sm font-semibold">{title}</h3>
      <p className="mt-1 text-xs text-fg-secondary leading-relaxed">{children}</p>
    </div>
  )
}
